import React, { FunctionComponent, useEffect, useState } from "react"
import { IconContext } from "react-icons/lib"
import {
	RiPencilFill,
	RiInboxUnarchiveLine,
	RiInboxArchiveFill,
	RiAddFill
} from "react-icons/ri"
import ColumnGroupTable from "@murphy-frontend/web-core/components/ColumnGroupTable"
import TableCellActionDropDownButton from "@murphy-frontend/web-core/components/ColumnGroupTable/TableCellActionDropDownButton"
import {
	getLargeColSizeBasedOnScreen,
} from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils"
import useWindowDimensions from "@murphy-frontend/web-core/hooks/useWindowDimensions"
import { createColumnGroup, sortByKey } from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils"
import { dashboardTypes } from "@murphy-frontend/web-core/constants"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { translateConstants } from "../../../common/services/translationService"
import CreateTemplate from "./CreateTemplate"
import { useCustomer } from "../../../common/contexts/CustomerContext"

export interface DashboardsListViewModel {
	id: string
	dashboardname: string
	isarchived: boolean
	link: string
	dashboardtypeid: number
	createddatetime: any
	lastupdatedatetime: any
}

interface DashboardsListProps {
	dashboards: DashboardsListViewModel[]
	onClickGoToDashboard: (dashboard: DashboardsListViewModel) => void
	onClickEditDashboard: (dashboard: DashboardsListViewModel) => void
	onClickArchiveDashboard: (dashboard: DashboardsListViewModel) => void
	onClickActivateDashboard: (dashboard: DashboardsListViewModel) => void
	translations: Record<string, string>
	language: string,
	style?: any
}

const DashboardsList: FunctionComponent<DashboardsListProps> = ({
	dashboards,
	onClickGoToDashboard,
	onClickEditDashboard,
	onClickArchiveDashboard,
	onClickActivateDashboard,
	translations,
	language,
	style = {}
}) => {
	const { currentSize: currentScreenSize } = useWindowDimensions()
	const [columnGroups, setColumnGroups] = useState([])
	const [currentSortingColumn, setCurrentSortingColumn] = useState("createddatetime")
	const [sortedAscending, setSortedAscending] = useState(false)
	const {openModal, closeModal} = useModal()
	const {customer} = useCustomer();
	const [visibleDropDownId, setVisibleDropDownId] = useState(null)

	const sortByColumn = (columnKey: any) => {
		setCurrentSortingColumn(columnKey)
		const newSortingOrder = !sortedAscending
		setSortedAscending(newSortingOrder)
	}

	const onClickCreateTemplate = (dashboardRow: DashboardsListViewModel) => {
		const modalConf = {
			title: translations.createtemplate,
			body: (
				<CreateTemplate
					row={dashboardRow}
					translations={translations}
					customer={customer}
					closeModal={closeModal}
					key={dashboardRow.id}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}
		openModal(modalConf)
	}

	const generateDropDownListItems = (dashboardRow: DashboardsListViewModel) => {
		const items = []
		items.push(
			<li key='edit' onClick={() => onClickEditDashboard(dashboardRow)}>
				<div className='vertical-aligner'>
					<IconContext.Provider value={{ className: "dropdown-icon" }}>
						<RiPencilFill />
					</IconContext.Provider>
					<span>{translations.edit}</span>
				</div>
			</li>
		)

		items.push(
			<li key={`create-${dashboardRow.id}`} onClick={() => onClickCreateTemplate(dashboardRow)}>
				<div className='vertical-aligner'>
					<IconContext.Provider value={{ className: "dropdown-icon" }}>
						<RiAddFill />
					</IconContext.Provider>
					<span>{translations.createtemplate}</span>
				</div>
			</li>
		)

		if (dashboardRow.isarchived === false) {
			items.push(
				<li key='archive' onClick={() => onClickArchiveDashboard(dashboardRow)}>
					<div className='vertical-aligner'>
						<IconContext.Provider value={{ className: "dropdown-icon" }}>
							<RiInboxArchiveFill />
						</IconContext.Provider>
						<span>{translations.archive}</span>
					</div>
				</li>
			)
		} else {
			items.push(
				<li key='activate' onClick={() => onClickActivateDashboard(dashboardRow)}>
					<div className='vertical-aligner'>
						<IconContext.Provider value={{ className: "dropdown-icon" }}>
							<RiInboxUnarchiveLine />
						</IconContext.Provider>
						<span>{translations.activate}</span>
					</div>
				</li>
			)
		}

		return items
	}

	const createMainColumnGroup = (dashboardsArray: DashboardsListViewModel[]) => {
		const dashboardNameColumns = [
			{
				columnKey: "dashboardname",
				displayName: translations.name,
				isSortable: true,
				size: 'column-large',
				onClickColumn: sortByColumn
			}
		]
		const dashboardNameRows = dashboardsArray.map(row => [
			{
				columnKey: "dashboardname",
				id: row.id,
				rowId: row.id,
				title: row.dashboardname,
				displayValue: (
					<div className="vertical-aligner space-between-flex" onClick={() => onClickGoToDashboard(row)} style={{ cursor: 'pointer' }}>
						<span>
							{row.dashboardname}
						</span>
					</div>
				),
			}
		])
		const mainDashboardColumnGroup = createColumnGroup("", dashboardNameColumns, dashboardNameRows)
		return mainDashboardColumnGroup
	}

	const createStatusColumnGroup = (dashboardsArray: DashboardsListViewModel[]) => {
		const dasboardStatusCol = [
			{
				columnKey: "status",
				displayName: translations.status || "Status",
				isSortable: true,
				size: 'column-medium-large',
				onClickColumn: sortByColumn
			}
		]
		const dashboardStatusRows = dashboardsArray.map(row => [{
			columnKey: "status",
			id: `archived-${row.id}`,
			rowId: row.id,
			displayValue: <div style={{ backgroundColor: row.isarchived === false ? '#FCF4EE' : '#F6F6F5', borderRadius: 10, cursor: 'pointer', paddingLeft: 10, paddingRight: 10 }} onClick={() => onClickGoToDashboard(row)}>
				<p style={{ color: row.isarchived === false ? '#E59100' : '#626262' }}>
					{row.isarchived === false ? translations.onlyactive : translations.closed}
				</p>
			</div>
		}
		])
		const statusColGroup = createColumnGroup("", dasboardStatusCol, dashboardStatusRows)
		return statusColGroup;
	}

	const createAccessColumnGroup = (dashboardsArray: DashboardsListViewModel[]) => {
		const dashboardAccessCol = [
			{
				columnKey: "access",
				displayName: translations.access || "Access",
				isSortable: true,
				size: 'column-medium-large',
				onClickColumn: sortByColumn
			}
		]
		const dashboardAccessRows = dashboardsArray.map(row => [

			{
				columnKey: "access",
				id: `type-${row.id}`,
				rowId: row.id,
				displayValue: <p style={{ cursor: 'pointer' }} onClick={() => onClickGoToDashboard(row)}>{
					translateConstants(language, dashboardTypes).filter(
						dt => dt.id === row.dashboardtypeid
					)[0].value
				}</p>
			}
		]);

		const accessColGroup = createColumnGroup("", dashboardAccessCol, dashboardAccessRows)
		return accessColGroup;
	}

	const createDateCreatedColumnGroup = (dashboardsArray: DashboardsListViewModel[]) => {
		const dateCol = [{
			columnKey: "createddatetime",
			displayName: translations.created,
			isSortable: true,
			size: 'column-medium-large',
			onClickColumn: sortByColumn
		}]
		const dateRows = dashboardsArray.map(row => [

			{
				columnKey: "createddatetime",
				id: `createddatetime-${row.id}`,
				rowId: row.id,
				displayValue: <TableCellActionDropDownButton
					onClickRow={() => onClickGoToDashboard(row)}
					displayText={row.createddatetime}
					listItems={generateDropDownListItems(row)}
					columnSize={getLargeColSizeBasedOnScreen(currentScreenSize)}
					showDropDown = {(id) => setVisibleDropDownId(id)}
					dropDownId = {visibleDropDownId}
					rowId = {row.id}
				/>
			}
		]);
		const dateColGroup = createColumnGroup("", dateCol, dateRows)
		return dateColGroup;
	}

	const createLastUpdatedColGroup = (dashboardsArray: DashboardsListViewModel[]) => {
		const lastUpdateCol = [
			{
				columnKey: "lastupdatedatetime",
				displayName: translations.lastUpdated,
				isSortable: true,
				size: 'column-medium-large',
				onClickColumn: sortByColumn
			}
		]
		const lastUpdatedRows = dashboardsArray.map(row => [

			{
				columnKey: "lastupdatedatetime",
				id: `lastupdatedatetime-${row.id}`,
				rowId: row.id,
				displayValue: <p style={{ cursor: 'pointer' }} onClick={() => onClickGoToDashboard(row)}>
					{row.lastupdatedatetime}
				</p>
			}
		])
		const lastUpdateColGroup = createColumnGroup("", lastUpdateCol, lastUpdatedRows)
		return lastUpdateColGroup;
	}

	useEffect(() => {
		if (dashboards) {
			const allColumnGroups = []
			const dashboardCopy = [...dashboards]

			if (currentSortingColumn) {
				dashboardCopy.sort(sortByKey(currentSortingColumn, sortedAscending))
			}

			const mainColGroup = createMainColumnGroup(dashboardCopy);
			const statusColGroup = createStatusColumnGroup(dashboardCopy);
			const accessColGroup = createAccessColumnGroup(dashboardCopy);
			const dateCreatedColGroup = createDateCreatedColumnGroup(dashboardCopy);
			const lastUpdatedColGroup = createLastUpdatedColGroup(dashboardCopy);

			allColumnGroups.push(mainColGroup)
			allColumnGroups.push(statusColGroup);
			allColumnGroups.push(accessColGroup);
			allColumnGroups.push(lastUpdatedColGroup);
			allColumnGroups.push(dateCreatedColGroup);

			setColumnGroups(allColumnGroups)
		}
	}, [dashboards, sortedAscending, currentSortingColumn, translations, language, currentScreenSize, visibleDropDownId])

	return (
		<ColumnGroupTable
			columngroups={columnGroups}
			sortedByColumn={currentSortingColumn}
			isSortedAscending={sortedAscending}
			translations={translations}
			style={style}
		/>
	)
}

export default DashboardsList
